<template>
  <section class="ae-card ae-conv" :class="{ 'is-compact': compact, 'is-working': thinking }" aria-label="Agent session">
    <div class="ae-card-head" :class="{ 'ae-card-head-stack': compact }">
      <div class="ae-title">{{ conversationTitle }}</div>
      <div class="ae-head-right">
        <a href="#" class="ae-link ae-btn-tiny ae-btn-icon" :title="sessionActive || s.connState !== 'idle' ? 'New session' : 'Start a session'" :aria-label="sessionActive || s.connState !== 'idle' ? 'New session' : 'Start a session'" @click.prevent="startLive"><font-awesome-icon :icon="sessionActive || s.connState !== 'idle' ? newIcon : startIcon" /></a>
        <a href="#" class="ae-link ae-btn-tiny ae-btn-icon" title="History" aria-label="History" @click.prevent="toggleHistory"><font-awesome-icon :icon="historyIcon" /></a>
        <a href="#" class="ae-link ae-btn-tiny ae-btn-icon" title="Scope: the tables and tools this agent may use" aria-label="Scope" @click.prevent="showScope = true"><font-awesome-icon :icon="scopeIcon" /></a>
        <a v-if="compact && entry.sessionId" href="#" class="ae-link ae-btn-tiny ae-btn-icon" title="Open this session on its own page, with the notebook and artifacts" aria-label="Open full" @click.prevent="$emit('open-full', entry.sessionId)"><font-awesome-icon :icon="expandIcon" /></a>
        <a v-if="compact" href="#" class="ae-close" title="Close" aria-label="Close" @click.prevent="$emit('close')"><font-awesome-icon :icon="closeIcon" /></a>
        <span class="ae-state">
          <span class="ae-dot" :class="{ 'is-live': thinking }" :style="{ background: stateDotColor }" />
          <span>{{ stateLabel }}</span>
          <span v-if="s.hat" class="ae-hat" :title="s.hat.description">{{ s.hat.label }}</span>
          <span v-if="s.totalCost" class="ae-cost">${{ s.totalCost.toFixed(2) }}</span>
          <span v-if="contextUsage" class="ae-cost" :class="contextUsage.tone" :title="`${s.promptTokens.toLocaleString()} of ${s.contextLimit.toLocaleString()} context tokens`">{{ contextUsage.text }}</span>
        </span>
      </div>
    </div>

    <div v-if="s.path && s.path.length" class="ae-path" aria-label="Session path">
      <span v-for="(p, i) in s.path" :key="p.name" class="ae-path-step" :class="'is-' + p.state" :title="p.state === 'current' ? 'Working on this now' : p.state === 'visited' ? 'Covered' : 'Still to do'">
        <span class="ae-path-mark">{{ p.state === 'visited' ? '✓' : p.state === 'current' ? '●' : '○' }}</span>{{ p.label }}<span v-if="i < s.path.length - 1" class="ae-path-sep">›</span>
      </span>
    </div>

    <div v-if="showHistory" class="ae-history-panel" aria-label="History">
      <div class="ae-card-head ae-card-head-stack">
        <div class="ae-title">History</div>
        <div class="ae-head-right">
          <span v-if="pastSessions.length" class="ae-state">{{ pastSessions.length }}</span>
          <a href="#" class="ae-link ae-btn-tiny" @click.prevent="showHistory = false">Close</a>
        </div>
      </div>
      <div class="ae-drawer-list">
        <p v-if="historyLoading" class="ae-muted" style="padding: 12px 16px">Loading…</p>
        <p v-else-if="!pastSessions.length" class="ae-muted" style="padding: 12px 16px">No sessions yet.</p>
        <button
          v-for="ps in drawerRows"
          :key="ps.session_id"
          type="button"
          class="ae-history-row"
          :class="{ 'is-open': ps.isOpen }"
          title="Open this session"
          @click="resumeSession(ps.session_id)"
        >
          <span class="ae-history-title">{{ ps.title || '(no message)' }}</span>
          <span v-if="ps.description" class="ae-history-desc">{{ ps.description }}</span>
          <span class="ae-history-meta">{{ formatWhen(ps.created_at) }}</span>
        </button>
      </div>
    </div>

    <div ref="scroll" class="ae-log">
      <div v-if="!s.blocks.length && !conn.sessionId" class="ae-empty">
        <div class="ae-empty-title">Start a session.</div>
        <div class="ae-empty-body">{{ onCanvas ? 'Ask about this workflow: which sources join, what the columns hold, a calculation to add, or a chart of the output.' : 'Every tool, no script. It waits for you.' }}</div>
      </div>

      <template v-for="(b, i) in s.blocks" :key="i">
        <div v-if="b.kind === 'user'" class="ae-row ae-row-user">
          <div class="ae-avatar ae-avatar-human">{{ userInitials }}</div>
          <div class="ae-col">
            <div class="ae-who">You</div>
            <div class="ae-bubble">{{ b.text }}</div>
          </div>
        </div>

        <div v-else-if="b.kind === 'text'" class="ae-row ae-row-agent">
          <div class="ae-avatar ae-avatar-agent">K</div>
          <div class="ae-col">
            <div class="ae-who">Agent</div>
            <template v-for="(seg, k) in segments(b.text, b.done !== false)" :key="k">
              <div v-if="seg.kind === 'prose'" class="ae-bubble ae-prose" v-html="renderMarkdown(seg.text)" />
              <a
                v-else-if="seg.kind === 'html'"
                href="#"
                class="ae-page-link"
                @click.prevent="$emit('goto-artifact', itemKey('page', pageIdent(seg.text)))"
              >Open the page in the workspace →</a>
              <div v-else class="ae-bubble ae-prose ae-building">Composing…</div>
            </template>
          </div>
        </div>

        <div v-else-if="b.kind === 'report'" class="ae-row ae-row-agent">
          <div class="ae-avatar ae-avatar-agent">K</div>
          <div class="ae-col">
            <div class="ae-who">Agent</div>
            <div class="ae-report">
              <div class="ae-report-title">{{ b.title }}</div>
              <div v-for="(it, j) in b.items" :key="j" class="ae-item">
                <span class="ae-check">✓</span>
                <span v-html="renderInline(it)" />
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="b.kind === 'approval'" class="ae-row ae-row-agent">
          <div class="ae-avatar ae-avatar-agent">K</div>
          <div class="ae-col ae-clarify">
            <div class="ae-clarify-q">Review this change · {{ b.operationKind }}</div>
            <pre class="ae-code">{{ JSON.stringify(b.payload, null, 2) }}</pre>
            <div v-if="!b.answered" class="ae-answer">
              <button type="button" class="ae-btn" :disabled="b.posting || !!s.viewer" @click="approveOperation(b, true)">Approve change</button>
              <button type="button" class="ae-btn" :disabled="b.posting || !!s.viewer" @click="approveOperation(b, false)">Decline</button>
            </div>
            <div v-else class="ae-answered">{{ b.answered }}</div>
            <div v-if="b.error" role="alert" class="ae-small">{{ b.error }}</div>
          </div>
        </div>

        <div v-else-if="b.kind === 'question'" class="ae-row ae-row-agent">
          <div class="ae-avatar ae-avatar-agent">K</div>
          <div class="ae-col">
            <div class="ae-who">Agent{{ b.answered ? '' : ' · waiting for your answer' }}</div>
            <div class="ae-clarify">
              <div class="ae-clarify-q" v-html="renderMarkdown(b.question)" />
              <button
                v-for="(option, j) in b.options"
                :key="option.label"
                type="button"
                class="ae-option"
                :class="{ 'is-chosen': b.answered === option.label, 'is-dim': b.answered && b.answered !== option.label }"
                :disabled="!!b.answered || !!s.viewer"
                @click="answerQuestion(b, option.label)"
              >
                <span class="ae-num">{{ j + 1 }}</span>
                <span><strong>{{ option.label }}</strong><span v-if="option.description"> — {{ option.description }}</span></span>
              </button>
              <div v-if="!b.answered" class="ae-answer">
                <input
                  v-model="b.responseText"
                  class="ae-answer-input"
                  placeholder="Or write your answer"
                  @keydown.enter.prevent="answerQuestion(b)"
                />
                <button type="button" class="ae-btn" :disabled="!b.responseText || !b.responseText.trim()" @click="answerQuestion(b)">Answer</button>
              </div>
              <div v-else class="ae-answered">
                {{ b.answered === '(expired)' ? 'Left unanswered in an earlier session.' : `Answer: ${b.answered}` }}
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="b.kind === 'thinking'" class="ae-aside">
          <button v-if="b.done && b.text.trim()" type="button" class="ae-link-muted" @click="b.collapsed = !b.collapsed">
            {{ b.collapsed ? 'Show reasoning' : 'Hide reasoning' }}
          </button>
          <div v-if="b.done && !b.collapsed" class="ae-reasoning">{{ b.text }}</div>
        </div>

        <div v-else-if="b.kind === 'proposal'" class="ae-proposal">
          <div class="ae-proposal-head">
            <strong>Proposed workflow</strong>
            <span v-if="b.name" class="ae-proposal-name">{{ b.name }}</span>
          </div>
          <p v-if="b.summary" class="ae-proposal-summary">{{ b.summary }}</p>
          <ol class="ae-plan">
            <li v-for="(step, si) in b.plan" :key="si" class="ae-plan-step" :class="{ 'is-done': si < b.appliedSteps }">
              <button type="button" class="ae-plan-head" :aria-expanded="b.openStep === si" @click="b.openStep = b.openStep === si ? -1 : si">
                <span class="ae-plan-chev">{{ b.openStep === si ? '▾' : '▸' }}</span>
                <span class="ae-plan-title">Step {{ si + 1 }}. {{ step.title }}</span>
                <span v-if="si < b.appliedSteps" class="ae-plan-check">✓</span>
              </button>
              <div v-if="b.openStep === si" class="ae-plan-body">
                <p v-if="step.description">{{ step.description }}</p>
                <p class="ae-proposal-meta">{{ proposalShape(step.studio_config) }}</p>
                <button type="button" class="ae-proposal-apply" :disabled="si !== b.appliedSteps || !onCanvas" @click="applyStep(b, si)">
                  {{ si < b.appliedSteps ? 'Applied' : 'Apply' }}
                </button>
              </div>
            </li>
          </ol>
          <button type="button" class="ae-proposal-apply ae-plan-all" :disabled="b.appliedSteps >= b.plan.length || !onCanvas" @click="applyStep(b, b.plan.length - 1)">
            {{ b.appliedSteps >= b.plan.length ? 'All applied' : (b.plan.length > 1 ? 'Apply all' : 'Apply to canvas') }}
          </button>
          <p v-if="!onCanvas && b.appliedSteps < b.plan.length" class="ae-proposal-meta">Open the Workflow Builder to apply these steps.</p>
        </div>

        <div v-else-if="b.kind === 'chart'" class="ae-chart">
          <div v-if="b.title" class="ae-chart-title">{{ b.title }}</div>
          <div class="ae-chart-body">
            <Chart_V6 v-if="chartView(b).component === 'chart_v6'" :spec="chartView(b).props.spec" />
            <table v-else class="ae-chart-table">
              <thead><tr><th v-for="c in chartView(b).props.columnDefs" :key="c.field">{{ c.headerName || c.field }}</th></tr></thead>
              <tbody>
                <tr v-for="(r, ri) in chartView(b).props.rowData" :key="ri">
                  <td v-for="c in chartView(b).props.columnDefs" :key="c.field">{{ r[c.field] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="ae-chart-meta">
            <span>{{ b.source }}</span>
            <template v-if="b.withheld"> · {{ b.withheld }} {{ b.withheld === 1 ? 'group' : 'groups' }} under 11 rows withheld</template>
          </p>
          <button v-if="b.spec && onCanvas" type="button" class="ae-proposal-apply" :disabled="b.savedAsSheet" @click="saveAsSheet(b)">
            {{ b.savedAsSheet ? 'Saved as a sheet' : 'Save as sheet' }}
          </button>
        </div>

        <div v-else-if="b.kind === 'created'" class="ae-aside">
          <button type="button" class="ae-created" :class="{ 'is-error': !b.ok }" @click="b.ok && b.itemKey && $emit('goto-artifact', b.itemKey)">
            <span class="ae-check" :style="b.ok ? '' : 'color:#c23a28'">{{ b.ok ? '✓' : '✕' }}</span>
            <span v-if="b.ok"><strong>{{ b.artifactLabel }} {{ b.action }}</strong> · <span>{{ b.name }}</span></span>
            <span v-else>{{ b.artifactLabel }} not saved · {{ b.error }}</span>
          </button>
        </div>

        <div v-else-if="b.kind === 'download'" class="ae-download">
          <font-awesome-icon :icon="pdfIcon" class="ae-download-icon" />
          <div class="ae-download-body">
            <div class="ae-download-title">{{ b.title }}</div>
            <div class="ae-download-meta">PDF<template v-if="b.size"> · {{ formatBytes(b.size) }}</template></div>
          </div>
          <a href="#" class="ae-link ae-btn-tiny" @click.prevent="downloadFile(b)">Download</a>
        </div>

        <div v-else-if="b.kind === 'upload'" class="ae-upload" :class="b.ok ? 'is-ok' : 'is-error'">
          <div class="ae-upload-title">{{ b.ok ? 'Uploaded' : 'Upload failed' }} · {{ b.name }}</div>
          <div v-if="b.ok && b.frames && b.frames.length" class="ae-upload-meta">Saved as {{ b.frames.length === 1 ? 'a table' : b.frames.length + ' tables' }}: {{ b.frames.map((f) => `${f.name} (${f.rows} rows)`).join(', ') }}</div>
          <div v-else-if="b.ok" class="ae-upload-meta ae-mono">{{ b.key }}</div>
          <div v-else class="ae-upload-meta">{{ b.error }}</div>
        </div>

        <div v-else-if="b.kind === 'action'" class="ae-aside ae-small ae-run-line">{{ actionVerb(b.verb) }} <span class="ae-mono">{{ b.targetId }}</span></div>
        <div v-else-if="b.kind === 'stopped'" class="ae-aside ae-small">Stopped.</div>
        <div v-else-if="b.kind === 'error'" class="ae-aside ae-small" style="color:#c23a28">{{ b.text }}</div>
        <div v-else-if="b.kind === 'meta'" class="ae-aside ae-small">{{ b.text }}</div>
      </template>
    </div>

    <div v-if="compact && notebookSummary" class="ae-notebook-hint">
      <span>{{ notebookSummary }}</span>
      <a href="#" class="ae-link" @click.prevent="$emit('open-full', entry.sessionId)">Open full</a>
    </div>

    <div v-if="thinking" class="ae-working-bar">
      <span class="ae-pulse" aria-hidden="true"><i /><i /><i /></span>
      <span class="ae-working-text">{{ workingText }}</span>
      <a href="#" class="ae-working-stop" @click.prevent="interrupt">Stop</a>
    </div>
    <div v-if="s.viewer" class="ae-notebook-hint">
      <span>Viewing <span class="sensitive">{{ s.viewer.owner || 'another user' }}</span>'s session in {{ s.viewer.tenant_name }}. Read-only.</span>
    </div>
    <div v-if="tenantMove" class="ae-notebook-hint">
      <span>This session is in {{ tenantMove.name }}. You're viewing {{ currentTenantName }}.</span>
      <button type="button" class="ae-btn ae-btn-primary" @click="switchToSessionTenant">Switch to {{ tenantMove.name }}</button>
    </div>
    <div class="ae-composer">
      <div class="ae-composer-box" :class="{ 'is-disabled': composerDisabled }">
        <textarea
          v-model="draft"
          rows="1"
          class="ae-textarea"
          :placeholder="composerPlaceholder"
          :disabled="composerDisabled"
          @keydown.enter.exact.prevent="sendDraft"
        />
        <button v-if="thinking" type="button" class="ae-btn" @click="interrupt">Stop</button>
        <button type="button" class="ae-btn ae-btn-primary" :disabled="!draft.trim() || composerDisabled" @click="sendDraft">Send</button>
      </div>
    </div>

    <AgentScopeModal v-if="showScope" :tools="s.tools" :hat="s.hat" @close="showScope = false" />
  </section>
</template>

<script>
import axios from 'axios';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { faUpRightAndDownLeftFromCenter, faFilePdf, faXmark, faPlay, faPlus, faClockRotateLeft, faSliders } from '@fortawesome/free-solid-svg-icons';
import { beginSensitiveEgress } from '@/utils/demoEgress';
import AgentScopeModal from './AgentScopeModal.vue';
import Chart_V6 from '@/components/widgetcomponents/Viz_Elements/Chart_V6.vue';
import { buildVizProps } from '@/utils/metricInsightAdapter';
import { canvasOnScreen } from '@/services/canvasContext';
import './agent.css';
import {
  hasPendingQuestion, wakePolling, stopPolling, replayHistory, fetchItems, pushBlock, turnUrl, applyEnvelope, attachFile, itemKey,
} from '@/services/agentSessionController';

marked.setOptions({ breaks: true, gfm: true });

const ACTION_VERBS = { run: 'Running', preview: 'Previewing', save: 'Saving', discard: 'Discarding' };
const ACTION_TEXT = {
  run: (id) => `Run notebook block \`${id}\` as it is now.`,
  preview: (id) => `Preview draft \`${id}\`.`,
  save: (id) => `Save draft \`${id}\` to the platform.`,
  discard: (id) => `Discard draft \`${id}\`.`,
};

export default {
  name: 'AgentConversation',
  components: { AgentScopeModal, Chart_V6 },
  props: {
    entry: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
    sessionId: { type: String, default: null },
    storageKey: { type: String, default: '' },
    compact: { type: Boolean, default: false },
    autoStart: { type: Boolean, default: false },
  },
  emits: ['session', 'reset', 'goto-artifact', 'open-full', 'close', 'apply-proposal', 'save-sheet'],
  data() {
    return {
      expandIcon: faUpRightAndDownLeftFromCenter,
      pdfIcon: faFilePdf,
      closeIcon: faXmark,
      startIcon: faPlay,
      newIcon: faPlus,
      historyIcon: faClockRotateLeft,
      scopeIcon: faSliders,
      conn: this.entry,
      s: this.entry.state,
      tenantMove: null,
      draft: '',
      now: Date.now(),
      ticker: null,
      showHistory: false,
      pastSessions: [],
      historyLoading: false,
      resumeId: null,
      continueFrom: '',
      showScope: false,
    };
  },
  computed: {
    currentTenantName() {
      return this.$store.getters['tenant/current_tenant_name'] || 'another tenant';
    },
    sessionActive() {
      return ['connecting', 'launching', 'ready', 'thinking', 'rate_limited'].includes(this.s.connState);
    },
    thinking() {
      return this.s.connState === 'thinking';
    },
    liveThinkingBlock() {
      return this.s.blocks.some((b) => b.kind === 'thinking' && !b.done);
    },
    hasPendingQuestion() {
      return hasPendingQuestion(this.conn);
    },
    composerDisabled() {
      return !!this.s.viewer || this.hasPendingQuestion || !this.conn.sessionId || this.s.connState === 'connecting';
    },
    contextUsage() {
      const used = this.s.promptTokens;
      const limit = this.s.contextLimit;
      if (typeof used !== 'number' || !limit) return null;
      const pct = used / limit;
      const fmt = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}k` : String(n));
      const tone = pct >= 0.9 ? 'is-error' : pct >= 0.7 ? 'is-stale' : '';
      return { text: `${fmt(used)} / ${fmt(limit)}${pct >= 0.9 ? ' · continue in a new session' : ''}`, tone };
    },
    thinkingElapsed() {
      if (!this.s.thinkingSince) return '';
      const secs = Math.max(0, Math.floor((this.now - this.s.thinkingSince) / 1000));
      return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
    },
    workingText() {
      return this.thinkingElapsed && this.thinkingElapsed !== '0s' ? `Working · ${this.thinkingElapsed}` : 'Working…';
    },
    stateLabel() {
      const st = this.s.connState;
      if (st === 'idle') return 'No session';
      if (st === 'connecting') return 'Connecting…';
      if (st === 'launching') return 'Starting the agent. You can send while it loads.';
      if (st === 'ready') return this.s.workerAlive ? 'Ready' : 'Off';
      if (st === 'thinking') return `Working · ${this.thinkingElapsed}`;
      if (st === 'rate_limited') return `Rate limited · retrying in ${this.s.retryInSeconds || '?'}s`;
      return this.s.closeReason || 'Disconnected';
    },
    stateDotColor() {
      const st = this.s.connState;
      if (st === 'ready') return this.s.workerAlive ? '#7aae8e' : '#b0aca3';
      if (st === 'thinking') return '#35617f';
      if (st === 'rate_limited' || st === 'launching' || st === 'connecting') return '#d9a441';
      return '#b0aca3';
    },
    drawerRows() {
      return this.pastSessions.map((ps) => {
        const isOpen = ps.session_id === this.conn.sessionId && this.sessionActive;
        return {
          ...ps,
          isOpen,
          title: isOpen && this.s.title ? this.s.title : ps.title,
          description: isOpen ? this.s.description : ps.description,
        };
      });
    },
    conversationTitle() {
      if (this.compact) return 'Interactive Agent';
      if (this.s.title) return this.s.title.length > 90 ? `${this.s.title.slice(0, 90)}…` : this.s.title;
      if (this.context.metricId) return `Metric ${this.context.metricId}`;
      if (this.context.studyId) return `Study ${this.context.studyId}`;
      if (this.context.s3Key) return this.context.s3Key.split('/').pop();
      return 'Interactive Agent';
    },
    onCanvas() {
      return canvasOnScreen();
    },
    userInitials() {
      const u = (this.$store && this.$store.state && this.$store.state.user) || {};
      const name = u.full_name || u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '';
      return String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'Y';
    },
    persistedCount() {
      return this.s.items.filter((a) => a.live || (a.kind === 'draft' && a.data.saved_id)).length;
    },
    notebookSummary() {
      const blocks = this.s.items.filter((a) => a.kind === 'cell').length;
      const drafts = this.s.items.filter((a) => a.kind === 'draft').length;
      const parts = [];
      if (blocks) parts.push(`${blocks} ${blocks === 1 ? 'block' : 'blocks'}`);
      if (drafts) parts.push(`${drafts} ${drafts === 1 ? 'draft' : 'drafts'}`);
      if (this.persistedCount) parts.push(`${this.persistedCount} saved`);
      return parts.length ? `In the notebook: ${parts.join(' · ')}` : '';
    },
    composerPlaceholder() {
      if (this.s.viewer) return `Read-only: this is ${this.s.viewer.owner || 'another user'}'s session`;
      if (this.s.connState === 'closed') return this.stateLabel;
      if (this.s.connState === 'idle') return 'Start a session to begin';
      if (this.hasPendingQuestion) return 'Answer the decision above to continue';
      return 'Describe what you need… (Enter to send)';
    },
  },
  watch: {
    sessionId(id) {
      if (id && id !== this.conn.sessionId) this.attachTo(id);
    },
  },
  async mounted() {
    this.ticker = setInterval(() => { this.now = Date.now(); }, 1000);
    this.conn.onWorkerOff = () => this.ensureWorker();
    this.scrollChat();
    if (this.sessionId && this.sessionId !== this.conn.sessionId) {
      await this.attachTo(this.sessionId);
      return;
    }
    if (this.sessionActive) {
      this.$emit('session', this.conn.sessionId);
      return;
    }
    const reattached = await this.reattach();
    if (!reattached && this.autoStart) this.connect();
  },
  beforeUnmount() {
    clearInterval(this.ticker);
    if (this.conn.onWorkerOff) this.conn.onWorkerOff = null;
  },
  methods: {
    saveAsSheet(block) {
      const s = block.spec || {};
      block.savedAsSheet = true;
      this.$emit('save-sheet', {
        name: block.title || s.title || 'Agent chart',
        spec: {
          type: s.type || 'bar', x: s.x || null, series: s.series || null,
          measure: s.measure || { agg: 'count' }, filters: s.filters || [],
          top: s.sort === 'value_desc' && s.limit ? s.limit : null,
        },
      });
    },
    applyStep(block, index) {
      const step = block.plan[index];
      block.appliedSteps = index + 1;
      block.openStep = index + 1 < block.plan.length ? index + 1 : -1;
      this.$emit('apply-proposal', { name: block.name, summary: step.title, studioConfig: step.studio_config });
    },
    proposalShape(config) {
      const steps = (config && config.steps) || [];
      const parts = [`${steps.length} ${steps.length === 1 ? 'source' : 'sources'}`];
      const calc = (config && config.calculated_columns) || [];
      const filters = (config && config.output_filters) || [];
      if (calc.length) parts.push(`${calc.length} calculated ${calc.length === 1 ? 'column' : 'columns'}`);
      if (filters.length) parts.push(`${filters.length} ${filters.length === 1 ? 'filter' : 'filters'}`);
      return parts.join(' · ');
    },
    chartView(block) {
      const fields = block.vizType === 'chart_v6'
        ? (block.vizConfig.series || []).map((sp) => sp.value_field)
        : ['VALUE'];
      const rows = block.rows.filter((r) => fields.some((f) => r[f] !== null && r[f] !== undefined));
      return buildVizProps(block.vizType, block.vizConfig, rows);
    },
    // Splits an agent turn into prose and ```html fenced documents — the same
    // rule as the iPad app (AgentScreen.segments), so one answer renders the
    // same on both surfaces. `settled` means the turn is over: an unterminated
    // fence then renders what arrived instead of spinning forever.
    segments(text, settled) {
      const out = [];
      let rest = String(text || '');
      let pending = null;   // a kairon-sources fence applies to the NEXT document
      const takeSources = (prose) => {
        const fence = prose.match(/```kairon-sources\s*([\s\S]*?)```/);
        if (!fence) return prose;
        try {
          const parsed = JSON.parse(fence[1].trim());
          if (Array.isArray(parsed)) pending = parsed;
        } catch { pending = null; }
        return prose.replace(fence[0], '').trim();
      };
      let open = rest.indexOf('```html');
      while (open !== -1) {
        const before = takeSources(rest.slice(0, open).trim());
        if (before) out.push({ kind: 'prose', text: before });
        const after = rest.slice(open + 7);
        const close = after.indexOf('```');
        if (close !== -1) {
          const doc = after.slice(0, close).trim();
          if (doc) out.push({ kind: 'html', text: doc, sources: pending || [] });
          pending = null;
          rest = after.slice(close + 3);
        } else {
          const partial = after.trim();
          if (settled && partial) out.push({ kind: 'html', text: partial, sources: pending || [] });
          else out.push({ kind: 'building' });
          rest = '';
        }
        open = rest.indexOf('```html');
      }
      const tail = takeSources(rest.trim());
      if (tail) out.push({ kind: 'prose', text: tail });
      if (!out.length) out.push({ kind: 'prose', text: String(text || '') });
      return out;
    },
    // Stable id for the nth document of a turn, so the chat link and the
    // workspace artifact agree without the worker emitting an event.
    pageIdent(html) {
      const heading = /<h1[^>]*>([^<]{1,80})<\/h1>/i.exec(String(html || ''));
      return (heading ? heading[1].trim() : '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    },
    itemKey,
    renderMarkdown(text) {
      return DOMPurify.sanitize(marked.parse(String(text || '')));
    },
    renderInline(text) {
      return DOMPurify.sanitize(marked.parseInline(String(text || '')));
    },
    actionVerb(verb) {
      return ACTION_VERBS[verb] || 'Running';
    },
    formatWhen(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    resetConversation() {
      const s = this.s;
      s.closeReason = '';
      s.viewer = null;
      s.blocks = [];
      s.items = [];
      s.files = [];
      s.totalCost = 0;
      s.title = '';
      s.description = '';
      s.promptTokens = null;
      s.contextLimit = null;
      s.continuedBy = '';
      s.tools = null;
      this.conn.order = 0;
      this.conn.cursor = 0;
      this.conn.ownSeqs = new Set();
      this.$emit('reset');
    },
    remember(sessionId) {
      if (!this.storageKey) return;
      try { sessionStorage.setItem(this.storageKey, sessionId); } catch { /* fine */ }
    },
    forget() {
      if (!this.storageKey) return;
      try { sessionStorage.removeItem(this.storageKey); } catch { /* fine */ }
    },
    async connect() {
      const s = this.s;
      s.connState = 'connecting';
      this.resetConversation();
      try {
        const { data } = await axios.post('/api/impact_analytics/agent-sessions/start/', {
          protocol_version: 2,
          metric_id: this.context.metricId || null,
          study_id: this.context.studyId || null,
          s3_key: this.context.s3Key || '',
          schema_id: this.context.schemaId || null,
          hats: this.context.hats || '',
          resume_session_id: this.resumeId || '',
          continue_from: this.continueFrom || '',
        });
        this.conn.sessionId = data.session_id;
        this.conn.cursor = data.cursor || 0;
        this.remember(data.session_id);
        s.connState = 'launching';
        wakePolling(this.conn);
        this.$emit('session', data.session_id);
      } catch (err) {
        s.connState = 'closed';
        s.closeReason = (err && err.response && err.response.data
          && err.response.data.error) || 'Could not start the session.';
      }
    },
    async replay(sessionId) {
      const { data } = await axios.get(
        `/api/impact_analytics/agent-sessions/${sessionId}/stream/`,
        { params: { after: 0 } },
      );
      if (data.status !== 'active') return false;
      this.resetConversation();
      this.conn.sessionId = sessionId;
      const { data: stored } = await axios.get(`/api/impact_analytics/agent-sessions/${sessionId}/messages/`);
      replayHistory(this.conn, data.events || [], stored.messages || []);
      applyEnvelope(this.conn, data);
      fetchItems(this.conn);
      wakePolling(this.conn);
      this.remember(sessionId);
      this.$emit('session', sessionId);
      return true;
    },
    async reattach() {
      let stored = null;
      try { stored = this.storageKey ? sessionStorage.getItem(this.storageKey) : null; } catch { /* fine */ }
      if (!stored) return false;
      try {
        const live = await this.replay(stored);
        if (!live) this.forget();
        return live;
      } catch {
        return false;
      }
    },
    async attachTo(sessionId) {
      if (!sessionId) return;
      await this.loadExistingSession(sessionId);
    },
    async loadExistingSession(sessionId) {
      this.showHistory = false;
      this.tenantMove = null;
      stopPolling(this.conn);
      this.resetConversation();
      this.conn.sessionId = sessionId;
      this.remember(sessionId);
      this.$emit('session', sessionId);
      this.s.connState = 'connecting';

      try {
        const [{ data }, { data: stored }] = await Promise.all([
          axios.get(`/api/impact_analytics/agent-sessions/${sessionId}/stream/`, { params: { after: 0 } }),
          axios.get(`/api/impact_analytics/agent-sessions/${sessionId}/messages/`),
        ]);
        replayHistory(this.conn, data.events || [], stored.messages || []);
        applyEnvelope(this.conn, data);
        fetchItems(this.conn);
        this.scrollChat();
        await this.ensureWorker();
        wakePolling(this.conn);
      } catch (err) {
        this.s.connState = 'closed';
        this.s.closeReason = (err && err.response && err.response.data && err.response.data.error) || 'Failed to load session.';
        if (err && err.response && err.response.status === 404) await this.locateSession(sessionId);
      }
    },
    async locateSession(sessionId) {
      try {
        const { data } = await axios.get(`/api/impact_analytics/agent-sessions/${sessionId}/locate/`);
        if (sessionId !== this.conn.sessionId) return;
        if (data.tenant_slug && data.tenant_slug !== this.$store.getters['tenant/current_tenant_slug']) {
          this.tenantMove = { slug: data.tenant_slug, name: data.tenant_name || data.tenant_slug };
          this.s.closeReason = `This session is in ${this.tenantMove.name}`;
        }
      } catch {
        this.tenantMove = null;
      }
    },
    async switchToSessionTenant() {
      if (!this.tenantMove) return;
      await this.$store.dispatch('tenant/setTenant_slug', this.tenantMove.slug);
      this.$router.go(0);
    },
    async resumeSession(sessionId) {
      await this.loadExistingSession(sessionId);
    },
    async ensureWorker() {
      const entry = this.conn;
      if (!entry.sessionId || this.s.workerAlive || this.s.viewer) return;
      if (['launching', 'connecting', 'thinking'].includes(this.s.connState)) return;
      this.s.connState = 'launching';
      this.s.closeReason = '';
      try {
        await axios.post('/api/impact_analytics/agent-sessions/start/', {
          protocol_version: 2,
          metric_id: this.context.metricId || null,
          study_id: this.context.studyId || null,
          s3_key: this.context.s3Key || '',
          schema_id: this.context.schemaId || null,
          hats: this.context.hats || '',
          resume_session_id: entry.sessionId,
        });
      } catch (err) {
        this.s.connState = 'closed';
        this.s.closeReason = (err && err.response && err.response.data && err.response.data.error) || 'Could not start the agent.';
      }
    },
    startLive() {
      this.showHistory = false;
      this.resumeId = null;
      this.continueFrom = '';
      this.connect();
    },
    async toggleHistory() {
      this.showHistory = !this.showHistory;
      if (this.showHistory) {
        this.historyLoading = true;
        try {
          const { data } = await axios.get('/api/impact_analytics/agent-sessions/');
          this.pastSessions = Array.isArray(data) ? data : (data.results || []);
        } finally {
          this.historyLoading = false;
        }
      }
    },
    scrollChat() {
      this.$nextTick(() => {
        const el = this.$refs.scroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    async sendUserText(text, action = null) {
      const entry = this.conn;
      if (!text || !entry.sessionId) return false;
      if (action) {
        pushBlock(entry, { kind: 'action', verb: action.verb, targetId: action.id });
      } else {
        if (!this.s.title) this.s.title = text;
        pushBlock(entry, { kind: 'user', text });
      }
      this.scrollChat();

      if (!this.s.workerAlive) {
        await this.ensureWorker();
        if (this.s.connState === 'closed') return false;
      }

      this.s.connState = this.s.workerAlive ? 'thinking' : 'launching';
      this.s.thinkingSince = Date.now();
      entry.pendingTurn = true;
      entry.posting += 1;
      axios.post(turnUrl(entry), action ? { type: 'user', text, action } : { type: 'user', text })
        .then(({ data }) => {
          if (data.seq) entry.ownSeqs.add(data.seq);
          this.s.connState = data.launching ? 'launching' : 'thinking';
        })
        .catch((err) => {
          pushBlock(entry, {
            kind: 'error',
            text: (err && err.response && err.response.data
              && err.response.data.error) || 'Message failed to send.',
          });
        })
        .finally(() => { entry.posting -= 1; wakePolling(entry); });
      return true;
    },
    sendAction(verb, id) {
      return this.sendUserText(ACTION_TEXT[verb](id), { verb, id });
    },
    async sendPageSave(item, version) {
      const entry = this.conn;
      if (!entry.sessionId) return false;
      const name = item.data.title || item.id;
      const slug = String(name || 'page').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 120) || 'page';
      pushBlock(entry, { kind: 'user', text: `Save “${name}” (v${version.n})` });
      this.scrollChat();
      if (!this.s.workerAlive) {
        await this.ensureWorker();
        if (this.s.connState === 'closed') return false;
      }
      this.s.connState = this.s.workerAlive ? 'thinking' : 'launching';
      this.s.thinkingSince = Date.now();
      entry.pendingTurn = true;
      entry.posting += 1;
      const frame = {
        type: 'page_save', artifact: item.id, version: version.n, title: name, slug,
        description: item.data.description || '',
      };
      if (item.data.page_id) frame.page_id = item.data.page_id;
      if (version.spec) frame.spec = version.spec;
      else { frame.html = version.html; frame.sources = version.sources || []; }
      axios.post(turnUrl(entry), frame)
        .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
        .catch((err) => {
          pushBlock(entry, { kind: 'error', text: err?.response?.data?.error || 'The save could not be sent.' });
          entry.pendingTurn = false;
        })
        .finally(() => { entry.posting -= 1; wakePolling(entry); });
      return true;
    },
    formatBytes(n) {
      if (!n) return '';
      return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
    },
    async downloadFile(file) {
      if (!this.conn.sessionId) return;
      const ticket = beginSensitiveEgress('download');
      if (!ticket) return;
      let response;
      try {
        response = await axios.get(`/api/impact_analytics/agent-sessions/${this.conn.sessionId}/download/`, {
          params: { key: file.s3Key }, responseType: 'blob',
        });
      } catch (err) {
        pushBlock(this.conn, { kind: 'error', text: `${file.name} could not be downloaded (HTTP ${err?.response?.status || 'error'}).` });
        return;
      }
      if (!ticket.confirm()) return;
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    },
    sendPageContext(page) {
      const entry = this.conn;
      if (!page || !entry.sessionId || !this.sessionActive) return false;
      axios.post(turnUrl(entry), { type: 'page_context', page })
        .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
        .catch(() => {});
      return true;
    },
    sendDraft() {
      if (this.sendUserText(this.draft.trim())) this.draft = '';
    },
    attachUpload({ name, key, frames }) {
      const entry = this.conn;
      pushBlock(entry, { kind: 'upload', ok: true, name, key, frames: frames || [] });
      attachFile(entry, name, key);
      if (!entry.sessionId || !this.sessionActive) return;
      entry.posting += 1;
      axios.post(turnUrl(entry), { type: 'file_attached', name, key, frames: frames || [] })
        .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
        .catch((err) => {
          pushBlock(entry, {
            kind: 'error',
            text: (err && err.response && err.response.data && err.response.data.error) || 'The agent was not told about this file.',
          });
        })
        .finally(() => { entry.posting -= 1; });
    },
    uploadFailed({ name, error }) {
      pushBlock(this.conn, { kind: 'upload', ok: false, name, error });
    },
    async approveOperation(block, approved) {
      if (block.answered || block.posting) return;
      block.posting = true;
      block.error = '';
      try {
        await axios.post(turnUrl(this.conn), {
          type: 'approval', operation_id: block.operationId, approved,
        });
        block.answered = approved ? 'Approved' : 'Declined';
        wakePolling(this.conn);
      } catch (error) {
        block.error = error.response?.data?.error || 'Could not record your decision. Retry to check the same proposal.';
      } finally {
        block.posting = false;
      }
    },
    answerQuestion(block, label = '') {
      const entry = this.conn;
      const text = (block.responseText || '').trim();
      if (block.answered || (!label && !text) || !entry.sessionId) return;
      block.answered = label || text;
      entry.posting += 1;
      axios.post(turnUrl(entry), {
        type: 'user_answer', call_id: block.callId, label, text,
      })
        .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
        .catch(() => {})
        .finally(() => { entry.posting -= 1; wakePolling(entry); });
    },
    interrupt() {
      const entry = this.conn;
      if (!entry.sessionId) return;
      axios.post(turnUrl(entry), { type: 'interrupt' })
        .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
        .catch(() => {})
        .finally(() => wakePolling(entry));
    },
  },
};
</script>
